import React, { MouseEvent, useContext, useRef, useState } from "react";
import { Body1, Body2, Header3, InteractiveElement, styled } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FormButtonsComponent, StyledCustomDialog } from "@iventis/components";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { MapObjectRepeatedTimeRangeValue } from "@iventis/types";
import { ErrorBoundaryContext, ErrorBoundaryFallback } from "@iventis/error-boundaries";
import { errorToErrorCode } from "@iventis/error-boundaries/src/error-code";
import { SentryErrorBoundary, SentryFallbackRender } from "@iventis/observability-and-monitoring/src/error-boundary/sentry-error-boundary";
import { RepeatedTimeRangeValueComponent, RepeatedTimeRangeValueRef } from "./repeated-time-range-value";

interface RepeatedTimeRangeModalProps {
    mapObjectName: string;
    layerName: string;
    attributeName: string;
    initialValues: MapObjectRepeatedTimeRangeValue[];
    saving: boolean;
    onSave: (values: MapObjectRepeatedTimeRangeValue[]) => void;
    onClose: () => void;
    onAttributeSettingsClicked: (e: MouseEvent) => void;
}

export const RepeatedTimeRangeModalComponent = ({
    mapObjectName,
    layerName,
    initialValues,
    attributeName,
    saving,
    onSave,
    onAttributeSettingsClicked,
    onClose,
}: RepeatedTimeRangeModalProps) => {
    const translate = useIventisTranslate();
    const errorContext = useContext(ErrorBoundaryContext);

    const [canSave, setCanSave] = useState(false);

    const ref = useRef<RepeatedTimeRangeValueRef>();

    const handleSavedClicked = () => {
        if (ref == null || ref.current == null) {
            throw new Error("ref is null or undefined");
        }
        onSave(ref.current.getValues());
    };

    const onError: SentryFallbackRender = ({ error, resetError }) => {
        errorContext.onError(errorToErrorCode(error as Error), error as Error);
        return <ErrorBoundaryFallback error={error} resetErrorBoundary={resetError} />;
    };

    return (
        <StyledCustomDialog maxWidth={false} open onClose={onClose}>
            <SentryErrorBoundary fallback={onError}>
                <StyledDialogTitle>
                    <div className="content">
                        <div className="layer-object-names">
                            <Header3 className="map-objet-name">{mapObjectName}</Header3>

                            <Body2 className="layer-name">{layerName}</Body2>
                        </div>
                        <InteractiveElement className="attribute-settings" onClick={(e) => onAttributeSettingsClicked(e)}>
                            <FontAwesomeIcon size="xl" icon={["far", "gear"]} />
                            <Body1>{translate(Content.map8.dates.attributeSettings)}</Body1>
                        </InteractiveElement>
                    </div>
                    <Divider />
                </StyledDialogTitle>
                <StyledDialogContent>
                    <RepeatedTimeRangeValueComponent attributeName={attributeName} initialValue={initialValues} ref={ref} setValidation={setCanSave} />
                </StyledDialogContent>
                <StyledDialogActions>
                    <FormButtonsComponent
                        submitButtonText={translate(Content.common.buttons.save)}
                        handleSubmit={handleSavedClicked}
                        disableSubmit={() => !canSave}
                        secondaryButtons={[{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: () => onClose() }]}
                        isSubmitting={saving}
                    />
                </StyledDialogActions>
            </SentryErrorBoundary>
        </StyledCustomDialog>
    );
};

const StyledDialogTitle = styled(DialogTitle)`
    .content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 65px;
        margin-bottom: 10px;
    }

    .attribute-settings-text {
        color: ${(props) => props.theme.typographyColors.core};
    }

    .attribute-settings {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
        color: ${(props) => props.theme.primaryColors.subduedMono};
    }

    .layer-object-names {
        display: flex;
        gap: 5px;
        flex-direction: column;
    }

    .map-object-name {
        font-weight: 600;
    }

    .layer-name {
        color: ${(props) => props.theme.shades.two};
    }
`;

const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 850px;
    height: 500px;
`;

const StyledDialogActions = styled(DialogActions)`
    padding: 20px;
`;
